/**
 * Accounts Module - TanStack Query Hooks
 *
 * This module provides all hooks for account-related operations including:
 * - Advisor data retrieval and updates
 * - Advisor credits management
 * - API token management (Express, Multireport, SIC)
 *
 * Note: Authentication operations (login, signup, password reset) are NOT included
 * as they happen before query client initialization and should remain as direct API calls.
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  changePassword,
  getAdvisorCredits,
  getAdvisorData,
  regenerateExpressToken,
  regenerateMultireportToken,
  regenerateSicToken,
  revokeExpressToken,
  updateAdvisor,
} from "api/accounts";

import { AccountData } from "js/components/types";

import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get current advisor data
 * @returns Advisor data with loading and error states
 */
export const useGetAdvisorData = () => {
  return useQuery({
    queryKey: queryKeys.accounts.advisor(),
    queryFn: getAdvisorData,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get advisor credits/quotes
 * @returns Advisor credits with loading and error states
 */
export const useGetAdvisorCredits = () => {
  return useQuery({
    queryKey: queryKeys.accounts.credits(),
    queryFn: getAdvisorCredits,
    staleTime: 1 * 60 * 1000, // 1 minute - credits change frequently
  });
};

// ============================================================================
// MUTATION HOOKS (UPDATE, TOKEN MANAGEMENT)
// ============================================================================

/**
 * Update advisor profile
 * Invalidates: advisor data, organization data
 */
export const useUpdateAdvisor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      advisorId,
      data,
    }: {
      advisorId: string;
      data: AccountData;
    }) => updateAdvisor(advisorId, data),
    onSuccess: () => {
      toast.success("Perfil actualizado exitosamente");
      // Invalidate advisor data
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.advisor() });
      // Invalidate organization data as advisor may be org member
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar el perfil";
      toast.error(errorMessage);
    },
  });
};

/**
 * Change user password
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data: { old_password: string; new_password: string }) =>
      changePassword(data),
    onSuccess: () => {
      toast.success("Contraseña actualizada exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar la contraseña";
      toast.error(errorMessage);
    },
  });
};

// ============================================================================
// TOKEN MANAGEMENT HOOKS
// ============================================================================

/**
 * Revoke Express API token
 * Invalidates: tokens data
 */
export const useRevokeExpressToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (advisorId: string) => revokeExpressToken(advisorId),
    onSuccess: () => {
      toast.success("Token revocado exitosamente");
      // Invalidate tokens
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.tokens() });
      // Invalidate advisor data as it may contain token info
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.advisor() });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al revocar el token";
      toast.error(errorMessage);
    },
  });
};

/**
 * Regenerate Express API token
 * Invalidates: tokens data
 * @returns New token data
 */
export const useRegenerateExpressToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (advisorId: string) => regenerateExpressToken(advisorId),
    onSuccess: () => {
      toast.success("Token Express regenerado exitosamente");
      // Invalidate tokens
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.tokens() });
      // Invalidate advisor data as it may contain token info
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.advisor() });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al regenerar el token Express";
      toast.error(errorMessage);
    },
  });
};

/**
 * Regenerate Multireport API token
 * Invalidates: tokens data
 * @returns New token data
 */
export const useRegenerateMultireportToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (advisorId: string) => regenerateMultireportToken(advisorId),
    onSuccess: () => {
      toast.success("Token Multireport regenerado exitosamente");
      // Invalidate tokens
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.tokens() });
      // Invalidate advisor data as it may contain token info
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.advisor() });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al regenerar el token Multireport";
      toast.error(errorMessage);
    },
  });
};

/**
 * Regenerate SIC API token
 * Invalidates: tokens data
 * @returns New token data
 */
export const useRegenerateSicToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (advisorId: string) => regenerateSicToken(advisorId),
    onSuccess: () => {
      toast.success("Token SIC regenerado exitosamente");
      // Invalidate tokens
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.tokens() });
      // Invalidate advisor data as it may contain token info
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.advisor() });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al regenerar el token SIC";
      toast.error(errorMessage);
    },
  });
};
